import { useState, useEffect } from "react";

// Approximate exchange rates (base: USD). Updated periodically.
const FX_TO_USD: Record<string, number> = {
  USD: 1, GBP: 0.74, EUR: 0.85, AUD: 1.41, CAD: 1.38, NZD: 1.71,
  JPY: 159, INR: 93, BRL: 5.04, ZAR: 16.4, SGD: 1.27, HKD: 7.83,
  CHF: 0.79, SEK: 9.27, NOK: 9.51, DKK: 6.37, MXN: 17.33, KRW: 1483,
  PLN: 3.63, CZK: 20.81, THB: 32.08, PHP: 59.94, MYR: 3.97, IDR: 17090,
};

// Country → currency mapping (covers ~95% of traffic)
const COUNTRY_CURRENCY: Record<string, string> = {
  US: "USD", GB: "GBP", AU: "AUD", NZ: "NZD", CA: "CAD", JP: "JPY",
  IN: "INR", BR: "BRL", ZA: "ZAR", SG: "SGD", HK: "HKD", CH: "CHF",
  SE: "SEK", NO: "NOK", DK: "DKK", MX: "MXN", KR: "KRW", PL: "PLN",
  CZ: "CZK", TH: "THB", PH: "PHP", MY: "MYR", ID: "IDR",
  // Eurozone
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", BE: "EUR",
  AT: "EUR", IE: "EUR", PT: "EUR", FI: "EUR", GR: "EUR", SK: "EUR",
  SI: "EUR", EE: "EUR", LV: "EUR", LT: "EUR", LU: "EUR", MT: "EUR",
  CY: "EUR", HR: "EUR",
};

// Currency display symbols
const SYMBOLS: Record<string, string> = {
  USD: "$", GBP: "£", EUR: "€", AUD: "$", CAD: "C$", NZD: "NZ$",
  JPY: "¥", INR: "₹", BRL: "R$", ZAR: "R", SGD: "S$", HKD: "HK$",
  CHF: "CHF ", SEK: "kr", NOK: "kr", DKK: "kr", MXN: "$", KRW: "₩",
  PLN: "zł", CZK: "Kč", THB: "฿", PHP: "₱", MYR: "RM", IDR: "Rp",
};

interface LocalPrice {
  /** e.g. "(~$2.82 AUD)" or null if same currency or unknown */
  bracket: string | null;
  loading: boolean;
}

/**
 * Given the site's native price and currency, returns a local-currency
 * estimate bracket string for the visitor's country.
 *
 * @param siteAmount  e.g. 1.49
 * @param siteCurrency e.g. "GBP"
 */
export function useLocalPrice(siteAmount: number, siteCurrency: string): LocalPrice {
  const [bracket, setBracket] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      try {
        // Check sessionStorage cache first
        let country = sessionStorage.getItem("_geo_country");
        if (!country) {
          const res = await fetch("/api/geo");
          if (res.ok) {
            const data = await res.json();
            country = data.country || null;
            if (country) sessionStorage.setItem("_geo_country", country);
          }
        }

        if (cancelled || !country) { setLoading(false); return; }

        const visitorCurrency = COUNTRY_CURRENCY[country];
        if (!visitorCurrency || visitorCurrency === siteCurrency) {
          // Same currency or unknown country — no bracket needed
          setLoading(false);
          return;
        }

        // Convert: site currency → USD → visitor currency
        const siteToUsd = FX_TO_USD[siteCurrency];
        const visitorToUsd = FX_TO_USD[visitorCurrency];
        if (!siteToUsd || !visitorToUsd) { setLoading(false); return; }

        const amountUsd = siteAmount / siteToUsd;
        const amountLocal = amountUsd * visitorToUsd;

        // Format
        const sym = SYMBOLS[visitorCurrency] || "";
        let formatted: string;
        if (amountLocal >= 1000) {
          formatted = `${sym}${Math.round(amountLocal).toLocaleString()}`;
        } else if (amountLocal >= 100) {
          formatted = `${sym}${Math.round(amountLocal)}`;
        } else {
          formatted = `${sym}${amountLocal.toFixed(2)}`;
        }

        setBracket(`(~${formatted} ${visitorCurrency})`);
      } catch {
        // Silent fail — bracket just won't show
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    detect();
    return () => { cancelled = true; };
  }, [siteAmount, siteCurrency]);

  return { bracket, loading };
}
